import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { MicButton } from "@/components/MicButton";
import { Container, PageHeader, Spinner, GenerateButton } from "@/components/shared";
import { toast } from "sonner";
import { Lightbulb, ChevronUp, ChevronDown, Plus, X } from "lucide-react";

const STATUS_META = {
  review: { label: "À l'étude", c: "bg-white/10 text-white/60" },
  planned: { label: "Prévu", c: "bg-blue-500/15 text-blue-300" },
  in_progress: { label: "En développement", c: "bg-gold/15 text-gold" },
  done: { label: "Disponible", c: "bg-emerald-500/15 text-emerald-300" },
  rejected: { label: "Refusé", c: "bg-red-500/15 text-red-300" },
};

export default function Ideas() {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("votes");
  const [showNew, setShowNew] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const load = async (s = sort) => {
    setLoading(true);
    try { const { data } = await api.get(`/ideas?sort=${s}`); setIdeas(data.ideas || []); }
    catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!title.trim()) return;
    try {
      await api.post("/ideas", { title, description });
      setTitle(""); setDescription(""); setShowNew(false);
      toast.success("Merci ! Votre idée est publiée, la communauté peut voter 💡");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const castVote = async (id, value) => {
    try {
      const { data } = await api.post(`/ideas/${id}/vote`, { value });
      setIdeas((prev) => prev.map((i) => i.idea_id === id
        ? { ...i, upvotes: data.upvotes, downvotes: data.downvotes, score: data.score, my_vote: data.my_vote } : i));
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Container>
      <PageHeader eyebrow="Idées de la communauté" title="Construisons BIZARRE AI ensemble"
        subtitle="Proposez vos idées, votez pour celles qui comptent. Les plus demandées façonnent la feuille de route." />

      <div className="flex items-center justify-between gap-3 my-6 flex-wrap">
        <GenerateButton data-testid="new-idea-button" onClick={() => setShowNew((s) => !s)}>
          {showNew ? <X size={16} /> : <Plus size={16} />} {showNew ? "Annuler" : "Proposer une idée"}
        </GenerateButton>
        <select data-testid="ideas-sort" value={sort} onChange={(e) => { setSort(e.target.value); load(e.target.value); }}
          className="bg-white/[0.04] border border-white/10 rounded-full px-4 py-2 text-sm">
          <option value="votes" className="bg-[#0a0a0a]">Les plus votées</option>
          <option value="recent" className="bg-[#0a0a0a]">Les plus récentes</option>
        </select>
      </div>

      {showNew && (
        <div className="card-surface p-5 mb-6 fade-up" data-testid="new-idea-form">
          <input data-testid="idea-title" value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Titre de votre idée"
            className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 outline-none text-sm mb-3 placeholder:text-white/30" />
          <textarea data-testid="idea-description" rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="Décrivez votre idée ou la fonctionnalité souhaitée…"
            className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 outline-none text-sm resize-none mb-3 placeholder:text-white/30" />
          <div className="flex items-center justify-between gap-3">
            <MicButton testid="idea-description-mic" value={description} onChange={setDescription} />
            <GenerateButton data-testid="submit-idea-button" onClick={submit} disabled={!title.trim()}>Publier l'idée</GenerateButton>
          </div>
        </div>
      )}

      {loading && <Spinner label="Chargement..." />}

      <div className="space-y-3">
        {ideas.map((idea) => {
          const st = STATUS_META[idea.status] || STATUS_META.review;
          return (
            <div key={idea.idea_id} data-testid={`idea-${idea.idea_id}`} className="card-surface p-5 flex gap-4">
              <div className="flex flex-col items-center gap-1 shrink-0">
                <button data-testid={`idea-up-${idea.idea_id}`} onClick={() => castVote(idea.idea_id, 1)}
                  className={`transition-colors ${idea.my_vote === 1 ? "text-gold" : "text-white/40 hover:text-gold"}`}>
                  <ChevronUp size={22} />
                </button>
                <span className="font-display text-lg">{idea.score}</span>
                <button data-testid={`idea-down-${idea.idea_id}`} onClick={() => castVote(idea.idea_id, -1)}
                  className={`transition-colors ${idea.my_vote === -1 ? "text-red-400" : "text-white/40 hover:text-red-400"}`}>
                  <ChevronDown size={22} />
                </button>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-3 mb-1">
                  <h3 className="font-medium text-white">{idea.title}</h3>
                  <span className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap ${st.c}`}>{st.label}</span>
                </div>
                {idea.description && <p className="text-white/60 text-sm">{idea.description}</p>}
                <div className="flex items-center gap-3 text-white/35 text-xs mt-2">
                  <span className="inline-flex items-center gap-1">👍🏾 {idea.upvotes}</span>
                  <span className="inline-flex items-center gap-1">👎🏾 {idea.downvotes}</span>
                  <span>· {idea.author_name}</span>
                </div>
              </div>
            </div>
          );
        })}
        {!loading && ideas.length === 0 && (
          <div className="text-center py-12">
            <Lightbulb size={32} className="text-gold/40 mx-auto mb-3" />
            <p className="text-white/40 text-sm">Aucune idée pour l'instant. Proposez la première !</p>
          </div>
        )}
      </div>
    </Container>
  );
}
